import {
  Alert,
  AlertIcon,
  Button,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
  HStack,
  useColorModeValue,
  Box,
  Divider,
} from '@chakra-ui/react';
import { faGear } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'urql';

import SparkelIcon from './icon/SparkelIcon';
import {
  CheckoutUrlDocument,
  GetSubscriptionInfoDocument,
  GetProductPricesDocument,
  ProductPrice,
} from 'src/gql/graphql';

type InsufficientCreditsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  creditsNeeded: number;
  creditsAvailable: number;
  /** If true, show the upgrade plan option (typically for free tier users) */
  showUpgradeOption?: boolean;
  /** If true, the credits are project-scoped */
  isProjectScoped?: boolean;
  /** Available credits in the tenant pool (for project-scoped scenario) */
  tenantCreditsAvailable?: number;
  /** If true, user is an admin */
  isAdmin?: boolean;
  /** Callback to open project settings */
  onOpenProjectSettings?: () => void;
  /** Next renewal date (ISO string) */
  renewalDate?: string;
  /** Amount of credits that will be granted on renewal */
  renewalAmount?: number;
};

// eslint-disable-next-line complexity
export function InsufficientCreditsModal({
  isOpen,
  onClose,
  creditsNeeded,
  creditsAvailable,
  showUpgradeOption = true,
  isProjectScoped = false,
  tenantCreditsAvailable = 0,
  isAdmin = false,
  onOpenProjectSettings,
  renewalDate,
  renewalAmount,
}: InsufficientCreditsModalProps) {
  const { t } = useTranslation('paywall');
  const [, createCheckoutUrl] = useMutation(CheckoutUrlDocument);

  // Query subscription info to check if user already has a subscription
  const [{ data: subscriptionData }] = useQuery({
    query: GetSubscriptionInfoDocument,
    pause: !isOpen, // Only query when modal is open
  });

  // Query product prices to display actual prices
  const [{ data: pricesData }] = useQuery({
    query: GetProductPricesDocument,
    variables: {
      productTypes: ['credit-pack-50', 'standard', 'pro'],
    },
    pause: !isOpen, // Only query when modal is open
  });

  const subscriptionInfo = subscriptionData?.mySubscriptionInfo;
  const currentTier = subscriptionInfo?.tier;
  const productPrices: ProductPrice[] = pricesData?.productPrices || [];

  // Helper to format price
  const formatPrice = (amount: number, currency = 'eur'): string => {
    const symbol =
      currency.toUpperCase() === 'EUR' ? '€' : currency.toUpperCase();
    return `${symbol}${(amount / 100).toFixed(0)}`;
  };

  // Get prices for display
  const creditPack50Price = productPrices.find(
    (p) => p.productType === 'credit-pack-50'
  );
  const standardMonthlyPrice = productPrices.find(
    (p) => p.productType === 'standard' && p.billingPeriod === 'monthly'
  );

  const creditsShort = Math.max(0, creditsNeeded - creditsAvailable);
  const hasTenantCreditsAvailable =
    isProjectScoped && tenantCreditsAvailable > 0;

  const subtitleColor = useColorModeValue('gray.600', 'gray.400');
  const renewalBoxBg = useColorModeValue('gray.50', 'gray.900');
  const renewalBoxBorder = useColorModeValue('gray.200', 'gray.700');

  const [, createCustomerPortalUrl] = useMutation(`
    mutation CreateCustomerPortalUrl($returnUrl: String, $flowType: String) {
      createCustomerPortalUrl(returnUrl: $returnUrl, flowType: $flowType)
    }
  `);

  const [, createCreditPacksCheckoutUrl] = useMutation(`
    mutation CreateCreditPacksCheckoutUrl($returnUrl: String) {
      createCreditPacksCheckoutUrl(returnUrl: $returnUrl)
    }
  `);

  const handleCheckout = async (
    productType: string,
    billingPeriod?: string
  ) => {
    try {
      const result = await createCheckoutUrl({
        productType: productType as any,
        billingPeriod: (billingPeriod || null) as any,
        returnUrl: window.location.href as any,
      });
      if (result.data?.createCheckoutUrl) {
        // Open checkout in new tab
        // The checkout tab will send a message when payment completes,
        // which is handled by CreditsIndicator's message listener
        window.open(result.data.createCheckoutUrl, '_blank');
        onClose();
      }
    } catch (error: any) {
      console.error('Failed to create checkout URL:', error);
      // Show error message to user
      alert(error?.message || 'Failed to create checkout. Please try again.');
    }
  };

  const handleManageSubscription = async (flowType?: string) => {
    try {
      const result = await createCustomerPortalUrl({
        returnUrl: window.location.href,
        flowType: flowType || undefined,
      });
      if (result.data?.createCustomerPortalUrl) {
        // Open customer portal in new tab
        window.open(result.data.createCustomerPortalUrl, '_blank');
        onClose();
      }
    } catch (error: any) {
      console.error('Failed to create customer portal URL:', error);
      alert(
        error?.message ||
          'Failed to open subscription management. Please try again.'
      );
    }
  };

  const handleManageSubscriptionClick = () => {
    void handleManageSubscription();
  };

  const handleUpgradeSubscriptionClick = () => {
    void handleManageSubscription('subscription_update');
  };

  const handleBuyCreditPacks = async () => {
    try {
      const result = await createCreditPacksCheckoutUrl({
        returnUrl: window.location.href,
      });
      if (result.data?.createCreditPacksCheckoutUrl) {
        // Open checkout in new tab
        window.open(result.data.createCreditPacksCheckoutUrl, '_blank');
        onClose();
      }
    } catch (error: any) {
      console.error('Failed to create credit packs checkout URL:', error);
      alert(
        error?.message ||
          'Failed to open credit packs checkout. Please try again.'
      );
    }
  };

  const handleOpenProjectSettings = () => {
    onOpenProjectSettings?.();
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent maxWidth="420px">
        <ModalHeader paddingBottom={2}>
          <Heading size="md">
            {creditsNeeded === 0
              ? t('insufficient-credits.add-credits-title', {
                  defaultValue: 'Add credits',
                })
              : t('insufficient-credits.title')}
          </Heading>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            {creditsNeeded > 0 && (
              <Text color={subtitleColor}>
                {t('insufficient-credits.body', {
                  needed: creditsNeeded,
                  available: creditsAvailable,
                  short: creditsShort,
                })}
              </Text>
            )}

            {hasTenantCreditsAvailable && (
              <Alert status="info" borderRadius="md">
                <AlertIcon />
                <Text fontSize="sm">
                  {isAdmin
                    ? t('insufficient-credits.project-empty-admin', {
                        tenantAvailable: tenantCreditsAvailable,
                      })
                    : t('insufficient-credits.project-empty-user', {
                        tenantAvailable: tenantCreditsAvailable,
                      })}
                </Text>
              </Alert>
            )}

            {renewalDate && renewalAmount !== undefined && (
              <Box
                padding={3}
                borderRadius="md"
                backgroundColor={renewalBoxBg}
                borderWidth="1px"
                borderColor={renewalBoxBorder}
              >
                <VStack spacing={1} align="stretch">
                  <Text fontSize="sm" fontWeight="medium" color={subtitleColor}>
                    {t('insufficient-credits.next-renewal', {
                      defaultValue: 'Next renewal',
                    })}
                  </Text>
                  <HStack justifyContent="space-between">
                    <Text fontSize="sm" color={subtitleColor}>
                      {new Date(renewalDate).toLocaleDateString(undefined, {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })}
                    </Text>
                    <Text fontSize="sm" fontWeight="medium">
                      {renewalAmount}{' '}
                      {t('insufficient-credits.credits', {
                        defaultValue: 'credits',
                      })}
                    </Text>
                  </HStack>
                </VStack>
              </Box>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <VStack width="100%" spacing={2}>
            {hasTenantCreditsAvailable && isAdmin && onOpenProjectSettings ? (
              <Button
                colorScheme="blue"
                borderRadius="md"
                width="100%"
                leftIcon={<SparkelIcon icon={faGear} color="inherit" />}
                onClick={handleOpenProjectSettings}
              >
                {t('insufficient-credits.allocate-credits')}
              </Button>
            ) : (
              <>
                <Button
                  size="sm"
                  colorScheme="orange"
                  borderRadius="md"
                  variant="outline"
                  width="100%"
                  onClick={handleBuyCreditPacks}
                >
                  {creditPack50Price
                    ? `Buy 50 credits - ${formatPrice(
                        creditPack50Price.amount,
                        creditPack50Price.currency
                      )}`
                    : t('insufficient-credits.buy-credit-packs', {
                        defaultValue: 'Buy Credit Packs',
                      })}
                </Button>
                {showUpgradeOption &&
                  (currentTier === 'pro' ? (
                    <Button
                      size="sm"
                      variant="outline"
                      borderRadius="md"
                      colorScheme="brand"
                      width="100%"
                      onClick={handleManageSubscriptionClick}
                    >
                      {t('insufficient-credits.manage-subscription', {
                        defaultValue: 'Manage Subscription',
                      })}
                    </Button>
                  ) : currentTier === 'standard' ? (
                    <Button
                      size="sm"
                      variant="outline"
                      colorScheme="brand"
                      borderRadius="md"
                      width="100%"
                      onClick={handleUpgradeSubscriptionClick}
                    >
                      {t('insufficient-credits.upgrade-subscription', {
                        defaultValue: 'Upgrade Subscription',
                      })}
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      colorScheme="brand"
                      borderRadius="md"
                      width="100%"
                      onClick={() => handleCheckout('standard', 'monthly')}
                    >
                      Standard - 300{' '}
                      {t('insufficient-credits.credits', {
                        defaultValue: 'credits',
                      })}
                      /month
                      {standardMonthlyPrice
                        ? ` - ${formatPrice(
                            standardMonthlyPrice.amount,
                            standardMonthlyPrice.currency
                          )}/month`
                        : ''}
                    </Button>
                  ))}
              </>
            )}
            <Divider />
            <Button
              variant="ghost"
              colorScheme="gray"
              width="100%"
              onClick={onClose}
              borderRadius="md"
            >
              {t('insufficient-credits.cancel')}
            </Button>
          </VStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
